import { defineMessages } from 'react-intl';
/**
 * Spoken names for the sky condition (FE #244).
 *
 * The backend computes an IBM / The Weather Company icon code (0-47) and sends
 * the bare integer; `apps/core/utils/weather/icons.py` holds English names for
 * all 48 but they never cross the wire. These are the client-side equivalent,
 * mapped by `Weather.getConditionMessage` in `@smartmixin/core`.
 *
 * Forty-three keys, not forty-eight: five day/night code pairs carry the same
 * name upstream (27/28, 29/30, 38/47, 39/45, 41/46), and the glyph already
 * carries the sun-vs-moon distinction for anyone who can see it.
 *
 * Two deliberate divergences from the backend table, both because these are
 * *spoken* strings rather than column headings:
 *
 * - **Sentence case**, matching `astral.newMoon` ("New moon") and
 *   `compass.N_long` ("North"), not the table's Title Case.
 * - **No slashes.** "Rain / Snow" is read as "rain slash snow" by some screen
 *   readers and silently skipped by others; "Rain and snow" survives both.
 *   For the same reason codes 33 and 34 become "Mostly clear" and "Mostly
 *   sunny" rather than collapsing to the table's shared "Fair" prefix, which
 *   would have made them indistinguishable by ear.
 *
 * Translators: `docs/weather-condition-glossary.md` spells out the
 * meteorological distinctions these names must preserve. Drizzle, showers and
 * rain are three different things, and so are sleet, freezing rain and hail.
 * `scripts/validate-weather-conditions.mjs` fails the build on an empty value,
 * a duplicate within a locale, or surviving English.
 */
export default defineMessages({
    blizzard: {
        id: 'weatherConditions.blizzard',
        defaultMessage: 'Blizzard'
    },
    blowingDust: {
        id: 'weatherConditions.blowingDust',
        defaultMessage: 'Blowing dust'
    },
    blowingSnow: {
        id: 'weatherConditions.blowingSnow',
        defaultMessage: 'Blowing snow'
    },
    breezy: {
        id: 'weatherConditions.breezy',
        defaultMessage: 'Breezy'
    },
    clear: {
        id: 'weatherConditions.clear',
        defaultMessage: 'Clear'
    },
    cloudy: {
        id: 'weatherConditions.cloudy',
        defaultMessage: 'Cloudy'
    },
    drizzle: {
        id: 'weatherConditions.drizzle',
        defaultMessage: 'Drizzle'
    },
    flurries: {
        id: 'weatherConditions.flurries',
        defaultMessage: 'Flurries'
    },
    foggy: {
        id: 'weatherConditions.foggy',
        defaultMessage: 'Fog'
    },
    freezingDrizzle: {
        id: 'weatherConditions.freezingDrizzle',
        defaultMessage: 'Freezing drizzle'
    },
    freezingRain: {
        id: 'weatherConditions.freezingRain',
        defaultMessage: 'Freezing rain'
    },
    frigid: {
        id: 'weatherConditions.frigid',
        defaultMessage: 'Frigid'
    },
    hail: {
        id: 'weatherConditions.hail',
        defaultMessage: 'Hail'
    },
    haze: {
        id: 'weatherConditions.haze',
        defaultMessage: 'Haze'
    },
    heavyRain: {
        id: 'weatherConditions.heavyRain',
        defaultMessage: 'Heavy rain'
    },
    heavySnow: {
        id: 'weatherConditions.heavySnow',
        defaultMessage: 'Heavy snow'
    },
    hot: {
        id: 'weatherConditions.hot',
        defaultMessage: 'Hot'
    },
    hurricane: {
        id: 'weatherConditions.hurricane',
        defaultMessage: 'Hurricane'
    },
    isolatedThunderstorms: {
        id: 'weatherConditions.isolatedThunderstorms',
        defaultMessage: 'Isolated thunderstorms'
    },
    mixedRainAndHail: {
        id: 'weatherConditions.mixedRainAndHail',
        defaultMessage: 'Rain and hail'
    },
    mostlyClear: {
        id: 'weatherConditions.mostlyClear',
        defaultMessage: 'Mostly clear'
    },
    mostlyCloudy: {
        id: 'weatherConditions.mostlyCloudy',
        defaultMessage: 'Mostly cloudy'
    },
    mostlySunny: {
        id: 'weatherConditions.mostlySunny',
        defaultMessage: 'Mostly sunny'
    },
    notAvailable: {
        id: 'weatherConditions.notAvailable',
        defaultMessage: 'Weather conditions unavailable'
    },
    partlyCloudy: {
        id: 'weatherConditions.partlyCloudy',
        defaultMessage: 'Partly cloudy'
    },
    rain: {
        id: 'weatherConditions.rain',
        defaultMessage: 'Rain'
    },
    rainSleet: {
        id: 'weatherConditions.rainSleet',
        defaultMessage: 'Rain and sleet'
    },
    rainSnow: {
        id: 'weatherConditions.rainSnow',
        defaultMessage: 'Rain and snow'
    },
    scatteredShowers: {
        id: 'weatherConditions.scatteredShowers',
        defaultMessage: 'Scattered showers'
    },
    scatteredSnowShowers: {
        id: 'weatherConditions.scatteredSnowShowers',
        defaultMessage: 'Scattered snow showers'
    },
    scatteredThunderstorms: {
        id: 'weatherConditions.scatteredThunderstorms',
        defaultMessage: 'Scattered thunderstorms'
    },
    showers: {
        id: 'weatherConditions.showers',
        defaultMessage: 'Showers'
    },
    sleet: {
        id: 'weatherConditions.sleet',
        defaultMessage: 'Sleet'
    },
    smoke: {
        id: 'weatherConditions.smoke',
        defaultMessage: 'Smoke'
    },
    snow: {
        id: 'weatherConditions.snow',
        defaultMessage: 'Snow'
    },
    snowShowers: {
        id: 'weatherConditions.snowShowers',
        defaultMessage: 'Snow showers'
    },
    strongStorms: {
        id: 'weatherConditions.strongStorms',
        defaultMessage: 'Strong storms'
    },
    sunny: {
        id: 'weatherConditions.sunny',
        defaultMessage: 'Sunny'
    },
    thunderstorms: {
        id: 'weatherConditions.thunderstorms',
        defaultMessage: 'Thunderstorms'
    },
    tornado: {
        id: 'weatherConditions.tornado',
        defaultMessage: 'Tornado'
    },
    tropicalStorm: {
        id: 'weatherConditions.tropicalStorm',
        defaultMessage: 'Tropical storm'
    },
    windy: {
        id: 'weatherConditions.windy',
        defaultMessage: 'Windy'
    },
    wintryMix: {
        id: 'weatherConditions.wintryMix',
        defaultMessage: 'Wintry mix'
    }
});
